import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCloseOnClickOutside } from '../../../hooks/useCloseOnClickOutside';
import api from '../../../config/axiosConfig';
import { API_URLS } from '../../../config/Constants';
import { sessionManager } from '../../../config/sessionManager';
import { showErrorMsg } from '../../../config/common';
import QuarterSelector from '../../Games/QuarterSelector';
import { ExportButton } from '../../../components/ExportButton';
import { ComprehensiveExportButton } from '../../../components/ComprehensiveExportButton';
import WomensPlayerTables, { buildWomensExportReports } from '../../../components/shared/WomensPlayerTables';
import PlayerNameButton from '../../../components/playerSheet/PlayerNameButton';
import {
  filterGamesByMode,
  formatLeaderboardGameLabel,
  GAME_MODE_PRACTICE,
  parseGameMode,
} from '../../../utils/seasonType';
import WomensFieldMark from '../../../components/shared/WomensFieldMark';

// Player types for each section
interface PlayerBase {
  playerNumber: string;
  playerName: string;
  playerId: string;
}

interface PossessionPlayer extends PlayerBase {
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
  goalsPlusMinus?: number;
  position?: string | string[];
  gamesPlayed?: number;
}

interface OffensePlayer extends PlayerBase {
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
  missedShots: number;
  position?: string | string[];
  gamesPlayed?: number;
}

interface GoaliePlayer extends PlayerBase {
  shotsOnGoal: number;
  saved: number;
  goalsAllowed: number;
  savePercentage: number;
  position?: string | string[];
  gamesPlayed?: number;
}

interface FaceoffPlayer extends PlayerBase {
  faceoffs: number;
  won: number;
  lost: number;
  winPercentage: number;
  lossPercentage: number;
  position?: string | string[];
  gamesPlayed?: number;
}

interface PenaltyPlayer extends PlayerBase {
  penalties: number;
  position?: string | string[];
  gamesPlayed?: number;
}

interface BlockedShotsPlayer extends PlayerBase {
  blocks: number;
  position?: string | string[];
  gamesPlayed?: number;
}

// Table component with sorting functionality
function StatsTable<T extends PlayerBase>({
  title,
  columns,
  players,
  isLoading,
  teamName,
  seasonName,
  clientId,
  sortConfig,
  onSortChange,
  cellPaddingY = 'py-0.5',
  cellPaddingX = 'px-0.5',
  cellFontSize = 'text-lg',
}: {
  title: string;
  columns: {
    header: string;
    key: keyof T | 'special';
    render?: (player: T) => React.ReactNode;
    sortable?: boolean;
  }[];
  players: T[];
  isLoading?: boolean;
  teamName?: string;
  seasonName?: string;
  clientId: string | null;
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  onSortChange?: (sortConfig: { key: string; direction: 'asc' | 'desc' } | null) => void;
  cellPaddingY?: string;
  cellPaddingX?: string;
  cellFontSize?: string;
}) {
  const toSortableNumber = (value: unknown): number | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') return null;

    const cleaned = value.trim().replace(/,/g, '').replace(/%$/, '');
    if (cleaned === '') return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Sort players based on current sort configuration
  const sortedPlayers = React.useMemo(() => {
    if (!sortConfig) return players;

    return [...players].sort((a, b) => {
      const { key, direction } = sortConfig;

      // Find the column configuration
      const column = columns.find((col) => {
        if (col.key === 'special') {
          if (col.header === 'Goals') return key === 'Goals';
          if (col.header === 'Saves') return key === 'Saves';
          if (col.header === 'PPG') return key === 'PPG';
          if (col.header === 'GPG') return key === 'GPG';
          if (col.header === 'Position') return key === 'position';
          if (col.header === 'Games Played' || col.header === 'GP') return key === 'gamesPlayed';
          if (col.header === 'Goals +/-' || col.header === 'G+/-') return key === 'goalsPlusMinus';
          if (col.header === 'Players') return key === 'player';
          if (col.header === 'CTO/PG') return key === 'CTO/PG';
          if (col.header === 'TO/PG') return key === 'TO/PG';
          if (col.header === 'Missed') return key === 'Missed';
          if (col.header === 'S%') return key === 'S%';
          if (col.header === 'SOG') return key === 'SOG';
          return false;
        }
        return String(col.key) === key;
      });
      if (!column) return 0;

      let aValue: any;
      let bValue: any;

      // Handle special rendering columns
      if (column.key === 'special' || column.render) {
        // For special columns, try to extract sortable values
        if (key === 'position') {
          const aPos = (a as any).position;
          const bPos = (b as any).position;
          aValue = Array.isArray(aPos) ? aPos.join(', ') : aPos || '';
          bValue = Array.isArray(bPos) ? bPos.join(', ') : bPos || '';
        } else if (key === 'gamesPlayed') {
          aValue = (a as any).gamesPlayed ?? 0;
          bValue = (b as any).gamesPlayed ?? 0;
        } else if (key === 'goalsPlusMinus') {
          aValue = (a as any).goalsPlusMinus ?? 0;
          bValue = (b as any).goalsPlusMinus ?? 0;
        } else if (key === 'player') {
          aValue = `${a.playerNumber} ${a.playerName}`;
          bValue = `${b.playerNumber} ${b.playerName}`;
        } else if (key === 'Goals') {
          aValue = Number((a as any).goals ?? 0);
          bValue = Number((b as any).goals ?? 0);
        } else if (key === 'S%') {
          aValue = Number((a as any).shotPercentage ?? 0);
          bValue = Number((b as any).shotPercentage ?? 0);
        } else if (key === 'SOG') {
          aValue = Number((a as any).sogPercentage ?? 0);
          bValue = Number((b as any).sogPercentage ?? 0);
        } else if (key === 'Saves') {
          aValue = (a as any).saved ?? 0;
          bValue = (b as any).saved ?? 0;
        } else if (key === 'PPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).points || 0) / aGames;
          bValue = ((b as any).points || 0) / bGames;
        } else if (key === 'GPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).goals || 0) / aGames;
          bValue = ((b as any).goals || 0) / bGames;
        } else if (key === 'CTO/PG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).causedTurnovers || 0) / aGames;
          bValue = ((b as any).causedTurnovers || 0) / bGames;
        } else if (key === 'TO/PG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).turnovers || 0) / aGames;
          bValue = ((b as any).turnovers || 0) / bGames;
        } else if (key === 'Missed') {
          aValue = ((a as any).shots || 0) - ((a as any).goals || 0);
          bValue = ((b as any).shots || 0) - ((b as any).goals || 0);
        } else {
          // Default to string comparison for other special columns
          aValue = String(a[column.key as keyof T] ?? '');
          bValue = String(b[column.key as keyof T] ?? '');
        }
      } else {
        // Regular column sorting
        aValue = a[column.key as keyof T];
        bValue = b[column.key as keyof T];
      }

      // Compare numeric values (supports numbers and numeric-like strings, including percents)
      const aNumber = toSortableNumber(aValue);
      const bNumber = toSortableNumber(bValue);
      if (aNumber !== null && bNumber !== null) {
        return direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [players, sortConfig, columns]);

  const handleSort = (key: string) => {
    if (!onSortChange) return;

    let direction: 'asc' | 'desc' = 'desc'; // Default to desc for stats
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }

    onSortChange({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 ml-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="mb-4">
      <div className="bg-sd-navy text-white text-lg font-bold px-4 py-2 flex items-center justify-between">
        <span>{title}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-normal hidden sm:block">Tap headers to sort</span>
          <ExportButton
            data={sortedPlayers}
            columns={columns.map((col) => ({
              header: col.header,
              key: String(col.key),
              dataKey: String(col.key),
            }))}
            title={title}
            teamName={teamName}
            seasonName={seasonName}
            size="sm"
            variant="outline"
            className="bg-white/10 border-white/30 hover:bg-white/20 text-xs px-2 py-1"
            sortConfig={sortConfig}
            clientId={clientId}
          />
        </div>
      </div>
      <div className="overflow-x-auto relative">
        {/* Loading Spinner */}
        {isLoading && (
          <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 z-50">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        )}
        <div className={`${isLoading ? 'opacity-20 pointer-events-none' : ''}`}>
          <table className="text-center border border-[#22c55e] min-w-full">
            <thead>
              <tr className="bg-sd-navy text-white">
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    onClick={() => {
                      let sortKey = String(col.key);
                      if (col.key === 'special') {
                        if (col.header === 'Position') sortKey = 'position';
                        else if (col.header === 'Games Played' || col.header === 'GP') sortKey = 'gamesPlayed';
                        else if (col.header === 'Goals' || col.header === 'G') sortKey = 'Goals';
                        else if (col.header === 'S%') sortKey = 'S%';
                        else if (col.header === 'SOG') sortKey = 'SOG';
                        else if (col.header === 'Saves' || col.header === 'SV') sortKey = 'Saves';
                        else if (col.header === 'PPG') sortKey = 'PPG';
                        else if (col.header === 'GPG') sortKey = 'GPG';
                        else if (col.header === 'Goals +/-' || col.header === 'G+/-') sortKey = 'goalsPlusMinus';
                        else if (col.header === 'Players') sortKey = 'player';
                        else if (col.header === 'CTO/PG') sortKey = 'CTO/PG';
                        else if (col.header === 'TO/PG') sortKey = 'TO/PG';
                        else if (col.header === 'Missed') sortKey = 'Missed';
                        else sortKey = `special_${idx}`;
                      }
                      handleSort(sortKey);
                    }}
                    className={`py-0.5 px-0.5 border border-[#22c55e] text-xs font-semibold cursor-pointer hover:bg-[#0a4c7a] select-none ${
                      idx === 0
                        ? 'text-left text-nowrap sticky left-0 bg-sd-navy z-10 hover:bg-[#0a4c7a]'
                        : 'text-center'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <span className={idx === 0 ? 'text-left flex-1' : ''}>{col.header}</span>
                      {(() => {
                        let sortKey = String(col.key);
                        if (col.key === 'special') {
                          if (col.header === 'Position') sortKey = 'position';
                          else if (col.header === 'Games Played' || col.header === 'GP') sortKey = 'gamesPlayed';
                          else if (col.header === 'Goals' || col.header === 'G') sortKey = 'Goals';
                          else if (col.header === 'S%') sortKey = 'S%';
                          else if (col.header === 'SOG') sortKey = 'SOG';
                          else if (col.header === 'Saves' || col.header === 'SV') sortKey = 'Saves';
                          else if (col.header === 'PPG') sortKey = 'PPG';
                          else if (col.header === 'GPG') sortKey = 'GPG';
                          else if (col.header === 'Goals +/-' || col.header === 'G+/-') sortKey = 'goalsPlusMinus';
                          else if (col.header === 'Players') sortKey = 'player';
                          else if (col.header === 'CTO/PG') sortKey = 'CTO/PG';
                          else if (col.header === 'TO/PG') sortKey = 'TO/PG';
                          else if (col.header === 'Missed') sortKey = 'Missed';
                          else sortKey = `special_${idx}`;
                        }
                        return getSortIcon(sortKey);
                      })()}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {players.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className="py-4 text-sm text-gray-500 text-center italic">
                    No players found for this section.
                  </td>
                </tr>
              </tbody>
            )}
            <tbody className="text-xs bg-[#f6fff0]">
              {sortedPlayers.map((player, idx) => (
                <tr key={player.playerId} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
                  {columns.map((col, colIdx) => (
                    <td
                      key={colIdx}
                      className={`${cellPaddingY} ${cellPaddingX} ${cellFontSize} border-2 ${
                        colIdx === 0
                          ? 'text-left left-0 sticky text-sm min-w-[85px] whitespace-pre-wrap bg-white'
                          : 'text-center'
                      }`}
                    >
                      {col.render ? col.render(player) : String(player[col.key as keyof T])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Main Component
const PlayersPageMobile: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gamesMode = parseGameMode(sessionManager.getGamesMode());

  // Team and season info from session storage
  const teamName = sessionManager.getClientName() || 'Team';
  const seasonName = sessionManager.getSeasonName() || 'Season';

  // Game / filter state
  const [games, setGames] = useState<any[]>([]);
  const [selectedGameOption, setSelectedGameOption] = useState<string>('ALL');
  const [selectedCustomGameIds, setSelectedCustomGameIds] = useState<string[]>([]);
  const [gameFilterReady, setGameFilterReady] = useState<boolean>(false);
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState<boolean>(false);
  const gameDropdownRef = useRef<HTMLDivElement>(null);
  const gameId =
    selectedGameOption !== 'ALL' && selectedGameOption !== 'LEAGUE' && selectedGameOption !== 'CUSTOM'
      ? selectedGameOption
      : null;

  // Period / quarter state
  const [periods, setPeriods] = useState<string[]>([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [activeQuarter, setActiveQuarter] = useState('Totals');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Player section state
  const [possessionPlayers, setPossessionPlayers] = useState<PossessionPlayer[]>([]);
  const [offensePlayers, setOffensePlayers] = useState<OffensePlayer[]>([]);
  const [goaliePlayers, setGoaliePlayers] = useState<GoaliePlayer[]>([]);
  const [faceoffPlayers, setFaceoffPlayers] = useState<FaceoffPlayer[]>([]);
  const [penaltyPlayers, setPenaltyPlayers] = useState<PenaltyPlayer[]>([]);
  const [rawPlayers, setRawPlayers] = useState<any[]>([]);
  const [blockedShotsPlayers, setBlockedShotsPlayers] = useState<BlockedShotsPlayer[]>([]);

  // Sort configurations for each table
  const [possessionSortConfig, setPossessionSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [offenseSortConfig, setOffenseSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [faceoffSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [penaltySortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [blockedShotsSortConfig, setBlockedShotsSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Column definitions
  const createPlayerColumn = <T extends PlayerBase>() => ({
    header: 'Players',
    key: 'special' as keyof T,
    render: (player: T) => (
      <PlayerNameButton playerSeasonId={player.playerId} seasonId={seasonId}>
        <span>
          <b>{player.playerNumber}</b> {player.playerName}
        </span>
      </PlayerNameButton>
    ),
  });

  // Helper function to abbreviate position names
  const abbreviatePosition = (position: string | string[]) => {
    if (Array.isArray(position)) {
      return position
        .map((pos) => (pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos))
        .join('\n');
    } else {
      return position === 'Long Stick Midfield'
        ? 'LSM'
        : position === 'Short Stick Defensive Midfield'
          ? 'SSDM'
          : position;
    }
  };

  const possessionColumns = [
    createPlayerColumn<PossessionPlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof PossessionPlayer,
      render: (p: PossessionPlayer) => (
        <span className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            '-'
          )}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof PossessionPlayer,
      render: (p: PossessionPlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'GB',
      key: 'groundBalls' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO',
      key: 'turnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO/PG',
      key: 'special' as keyof PossessionPlayer,
      render: (player: PossessionPlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.turnovers / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
    {
      header: 'CTO',
      key: 'causedTurnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'CTO/PG',
      key: 'special' as keyof PossessionPlayer,
      render: (player: PossessionPlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.causedTurnovers / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
    {
      header: 'SS',
      key: 'shotsSaved' as keyof PossessionPlayer,
      sortable: true,
    },
    { header: 'NPC', key: 'npc' as keyof PossessionPlayer, sortable: true },
    {
      header: 'G+/-',
      key: 'special' as keyof PossessionPlayer,
      render: (p: PossessionPlayer) => p.goalsPlusMinus ?? '',
      sortable: true,
    },
  ];

  const offenseColumns = [
    createPlayerColumn<OffensePlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof OffensePlayer,
      render: (p: OffensePlayer) => (
        <span className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            '-'
          )}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof OffensePlayer,
      render: (p: OffensePlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'S',
      key: 'shots' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'Missed',
      key: 'missedShots' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'SS',
      key: 'saved' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'G',
      key: 'goals' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'S%',
      key: 'special' as keyof OffensePlayer,
      render: (player: OffensePlayer) => (
        <div className="text-xs text-gray-600">{player.shotPercentage.toFixed(0)}%</div>
      ),
      sortable: true,
    },
    {
      header: 'SOG',
      key: 'special' as keyof OffensePlayer,
      render: (player: OffensePlayer) => <div className="text-xs text-gray-600">{player.sogPercentage}%</div>,
      sortable: true,
    },
    {
      header: 'A',
      key: 'assists' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'P',
      key: 'points' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'PPG',
      key: 'special' as keyof OffensePlayer,
      render: (player: OffensePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.points / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
    {
      header: 'GPG',
      key: 'special' as keyof OffensePlayer,
      render: (player: OffensePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.goals / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
  ];

  const goalieColumns = [
    createPlayerColumn<GoaliePlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof GoaliePlayer,
      render: (p: GoaliePlayer) => (
        <span className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            'G'
          )}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof GoaliePlayer,
      render: (p: GoaliePlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'SOG',
      key: 'shotsOnGoal' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'SV',
      key: 'special' as keyof GoaliePlayer,
      render: (player: GoaliePlayer) => (
        <div>
          <div>{player.saved}</div>
          <div className="text-xs text-gray-600">({player.savePercentage}%)</div>
        </div>
      ),
      sortable: true,
    },
    {
      header: 'SPG',
      key: 'special' as keyof GoaliePlayer,
      render: (player: GoaliePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.saved / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
    {
      header: 'GA',
      key: 'goalsAllowed' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'GAPG',
      key: 'special' as keyof GoaliePlayer,
      render: (player: GoaliePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.goalsAllowed / gamesPlayed).toFixed(1);
      },
      sortable: true,
    },
  ];

  const faceoffColumns = [
    createPlayerColumn<FaceoffPlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof FaceoffPlayer,
      render: (p: FaceoffPlayer) => (
        <span className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            '-'
          )}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof FaceoffPlayer,
      render: (p: FaceoffPlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'DC',
      key: 'faceoffs' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'W',
      key: 'special' as keyof FaceoffPlayer,
      render: (player: FaceoffPlayer) => (
        <div>
          <div>{player.won}</div>
          <div className="text-xs text-gray-600">({player.winPercentage.toFixed(0)}%)</div>
        </div>
      ),
      sortable: true,
    },
    {
      header: 'L',
      key: 'special' as keyof FaceoffPlayer,
      render: (player: FaceoffPlayer) => (
        <div>
          <div>{player.lost}</div>
        </div>
      ),
      sortable: true,
    },
  ];

  const penaltyColumns = [
    createPlayerColumn<PenaltyPlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof PenaltyPlayer,
      render: (p: PenaltyPlayer) => (
        <div className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            '-'
          )}
        </div>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof PenaltyPlayer,
      render: (p: PenaltyPlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'Penalties',
      key: 'penalties' as keyof PenaltyPlayer,
      sortable: true,
    },
  ];

  const blockedShotsColumns = [
    createPlayerColumn<BlockedShotsPlayer>(),
    {
      header: 'Position',
      key: 'special' as keyof BlockedShotsPlayer,
      render: (p: BlockedShotsPlayer) => (
        <span className="text-sm">
          {Array.isArray(p.position) ? (
            <div className="whitespace-pre-line">{abbreviatePosition(p.position)}</div>
          ) : p.position ? (
            abbreviatePosition(p.position)
          ) : (
            '-'
          )}
        </span>
      ),
      sortable: true,
    },
    {
      header: 'GP',
      key: 'special' as keyof BlockedShotsPlayer,
      render: (p: BlockedShotsPlayer) => p.gamesPlayed ?? 0,
      sortable: true,
    },
    {
      header: 'Blocked Shots',
      key: 'blocks' as keyof BlockedShotsPlayer,
      sortable: true,
    },
  ];

  // Initialization tracking
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!clientId || !seasonId) return;
    const init = async () => {
      fetchGames();
      fetchPeriods();
      if (!initializedRef.current && selectedGameOption === 'ALL') {
        const savedFilter = sessionManager.getSeasonGameFilter();
        if (savedFilter?.mode === 'league' && gamesMode !== GAME_MODE_PRACTICE) {
          setSelectedGameOption('LEAGUE');
          setSelectedCustomGameIds([]);
        } else if (savedFilter?.mode === 'custom') {
          setSelectedGameOption('CUSTOM');
          setSelectedCustomGameIds(savedFilter.customGameIds || []);
        } else if (savedFilter?.mode === 'single' && savedFilter.gameId) {
          setSelectedGameOption(savedFilter.gameId);
          setSelectedCustomGameIds([]);
        } else {
          setSelectedGameOption('ALL');
          setSelectedCustomGameIds([]);
        }
        setGameFilterReady(true);
      }
      initializedRef.current = true;
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, seasonId]);

  useEffect(() => {
    fetchPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  useEffect(() => {
    if (periods.length > 0) {
      fetchPlayerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuarter, gameId, selectedGameOption, selectedCustomGameIds, periods]);

  const fetchPeriods = async () => {
    try {
      if (!clientId || !seasonId) return;
      let response;
      if (gameId) {
        // Fetch periods for specific game
        response = await api.get(`${API_URLS.GET_GAME_PERIODS}/${gameId}/periods`, { params: { clientId, seasonId } });
      } else {
        // Fetch periods for season (when ALL games selected)
        response = await api.get(`${API_URLS.GET_SEASON_PERIOD}`, {
          params: { clientId, seasonId },
        });
      }

      if (response.data.success) {
        const data = response.data.data;
        const hp = data.halfPeriod;
        const otFlag = data.otFlag;
        let arr: string[] = hp ? ['Totals', 'H1', 'H2'] : ['Totals', 'Q1', 'Q2', 'Q3', 'Q4'];
        if (otFlag && !arr.includes('OT')) arr.push('OT');
        setPeriods((prev) => (prev.length === arr.length && prev.every((p, i) => p === arr[i]) ? prev : arr));
        setActiveQuarter('Totals');
        setPeriodsLoaded(true);
      }
    } catch (e) {
      if (!periodsLoaded) {
        setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4']);
        setActiveQuarter('Totals');
        setPeriodsLoaded(true);
      }
    }
  };

  const fetchGames = async () => {
    if (!clientId || !seasonId) return;
    try {
      const resp = await api.get(`${API_URLS.GET_SEASON_GAMES}`, {
        params: { clientId, seasonId, gameMode: gamesMode },
      });
      if (resp.data.success) setGames(resp.data.data || []);
    } catch (e) {
      console.error('Failed to fetch games list', e);
    }
  };

  const processPossessionData = useCallback((playersData: any[]) => {
    const processed: PossessionPlayer[] = playersData
      .filter((player) => {
        // Exclude pure goalies from possession stats
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        if (isOnlyGoalie) {
          const hasNonGoalieActivity =
            (player.groundballs || 0) > 0 || (player.turnovers || 0) > 0 || (player.causedturnover || 0) > 0;
          return hasNonGoalieActivity;
        }
        return true;
      })
      .map((player) => {
        const groundBalls = player.groundballs || 0;
        const turnovers = player.turnovers || 0;
        const causedTurnovers = player.causedturnover || 0;
        // For possession, shotsSaved refers to offensive players who had shots saved
        const shotsSaved = player.shotsaved || 0;

        // NPC Definition: NPC = Ground Balls - Net Turnovers (TO - CTOs) - Shots Saved
        // Net Turnovers = Turnovers - Caused Turnovers
        const netTurnovers = turnovers - causedTurnovers;
        const npc = groundBalls - netTurnovers - shotsSaved;
        const activity = groundBalls + turnovers + causedTurnovers + shotsSaved;

        const goalsPlusMinus = npc * 0.25;
        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          groundBalls,
          turnovers,
          causedTurnovers,
          shotsSaved,
          activity,
          npc,
          goalsPlusMinus,
        };
      })
      .filter((player) => player.activity > 0);

    setPossessionPlayers(processed);
  }, []);

  const processOffenseData = useCallback((playersData: any[]) => {
    const processed: OffensePlayer[] = playersData
      .filter((player) => {
        const shots = player.shots || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const hasOffensiveActivity = shots > 0 || goals > 0 || assists > 0;

        // Check if they're a pure goalie
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        if (isOnlyGoalie) {
          // Only include goalies in offense if they actually took shots
          return shots > 0;
        }

        return hasOffensiveActivity;
      })
      .map((player) => {
        const shots = player.shots || 0;
        // For offense, saved means this player's shots were saved
        const saved = player.shotsaved || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const missedShots = player.shotmissed || 0;
        const points = goals + assists;
        const shotPercentage = shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0;
        const sogPercentage = shots > 0 ? Math.ceil(((goals + saved) / shots) * 100) : 0;

        const activity = shots + saved + goals + assists;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shots,
          saved,
          goals,
          assists,
          points,
          shotPercentage,
          sogPercentage,
          missedShots,
          activity,
        };
      })
      .filter((player) => player.activity > 0);

    setOffensePlayers(processed);
  }, []);

  const processGoalieData = useCallback((playersData: any[]) => {
    const processed: GoaliePlayer[] = playersData
      .filter((player) => {
        // Include players who have goalie stats OR are designated as goalies
        const hasGoalieStats = (player.saved || 0) > 0 || (player.shotsongoal || 0) > 0;
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isGoalie = positions.includes('Goalie') || player.isGoalie;

        return hasGoalieStats || isGoalie;
      })
      .map((player) => {
        // For goalies: saved = saves they made, shotsongoal = shots faced
        const shotsOnGoal = player.shotsongoal || 0;
        const saved = player.saved || 0;
        const goalsAllowed = player.goalsallowed || shotsOnGoal - saved;
        const savePercentage = shotsOnGoal > 0 ? Number(((saved / shotsOnGoal) * 100).toFixed(0)) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions,
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shotsOnGoal,
          saved,
          goalsAllowed,
          savePercentage,
        };
      });

    setGoaliePlayers(processed);
  }, []);

  const processFaceoffData = useCallback((playersData: any[]) => {
    const processed: FaceoffPlayer[] = playersData
      .filter((player) => (player.faceoffwins || 0) > 0 || (player.faceofflosses || 0) > 0)
      .map((player) => {
        const won = player.faceoffwins || 0;
        const lost = player.faceofflosses || 0;
        const total = won + lost;
        const winPercentage = total > 0 ? Number(((won / total) * 100).toFixed(0)) : 0;
        const lossPercentage = total > 0 ? Number(((lost / total) * 100).toFixed(0)) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          faceoffs: total,
          won,
          lost,
          winPercentage,
          lossPercentage,
        };
      });

    setFaceoffPlayers(processed);
  }, []);

  const processPenaltyData = useCallback((playersData: any[]) => {
    const processed: PenaltyPlayer[] = playersData
      .filter((player) => (player.penalties || 0) > 0)
      .map((player) => ({
        playerId: player.playerSeasonId || player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        position: player.position || player.positions || [],
        gamesPlayed: player.gamesPlayed || player.gameCount || 0,
        penalties: player.penalties || 0,
      }));

    setPenaltyPlayers(processed);
  }, []);

  const processBlockedShotsData = useCallback((playersData: any[]) => {
    const processed: BlockedShotsPlayer[] = playersData
      .filter((player) => (player.blocks || 0) > 0)
      .map((player) => ({
        playerId: player.playerSeasonId || player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        position: player.position || player.positions || [],
        gamesPlayed: player.gamesPlayed || player.gameCount || 0,
        blocks: player.blocks || 0,
      }));

    setBlockedShotsPlayers(processed);
  }, []);

  const lastFetchKeyRef = useRef<string | null>(null);
  const fetchPlayerStats = useCallback(async () => {
    if (!clientId || !seasonId || !activeQuarter) return;
    if (selectedGameOption === 'CUSTOM' && selectedCustomGameIds.length === 0) {
      setPossessionPlayers([]);
      setOffensePlayers([]);
      setGoaliePlayers([]);
      setFaceoffPlayers([]);
      setPenaltyPlayers([]);
      setBlockedShotsPlayers([]);
      setRawPlayers([]);
      return;
    }
    const key = `${clientId}|${seasonId}|${activeQuarter}|${gameId || 'ALL'}|${selectedGameOption}|${selectedCustomGameIds.join(',')}|${gamesMode}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    setIsRefreshing(true);
    try {
      const handleData = (playersData: any[]) => {
        processPossessionData(playersData);
        processOffenseData(playersData);
        processGoalieData(playersData);
        processFaceoffData(playersData);
        processPenaltyData(playersData);
        processBlockedShotsData(playersData);
        setRawPlayers(Array.isArray(playersData) ? playersData : []);
      };
      const params: Record<string, string> = {
        clientId,
        seasonId,
        quarterId: activeQuarter,
        gameMode: gamesMode,
      };
      if (selectedGameOption === 'LEAGUE') params.league = 'true';
      else if (selectedGameOption === 'CUSTOM') params.gameIds = selectedCustomGameIds.join(',');
      else if (gameId) params.gameId = gameId;
      const response = await api.get(API_URLS.GET_PLAYER_STATS, { params });
      if (response.data.success) handleData(response.data.data);
      else showErrorMsg(response.data.message || 'Failed to fetch player statistics');
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch player statistics');
    } finally {
      setIsRefreshing(false);
    }
  }, [
    clientId,
    seasonId,
    activeQuarter,
    gameId,
    selectedGameOption,
    selectedCustomGameIds,
    gamesMode,
    processPossessionData,
    processOffenseData,
    processGoalieData,
    processFaceoffData,
    processPenaltyData,
    processBlockedShotsData,
  ]);

  useEffect(() => {
    if (games.length === 0) return;
    const validIds = new Set(filterGamesByMode(games, gamesMode).map((g) => String(g._id)));
    setSelectedCustomGameIds((prev) => prev.filter((id) => validIds.has(String(id))));
    setSelectedGameOption((prev) => {
      if (gamesMode === GAME_MODE_PRACTICE && prev === 'LEAGUE') return 'ALL';
      if (prev === 'ALL' || prev === 'LEAGUE' || prev === 'CUSTOM') return prev;
      return validIds.has(String(prev)) ? prev : 'ALL';
    });
  }, [games, gamesMode]);

  useEffect(() => {
    if (!gameFilterReady) return;
    if (selectedGameOption === 'LEAGUE') {
      sessionManager.setSeasonGameFilter({ mode: 'league' });
      return;
    }
    if (selectedGameOption === 'CUSTOM') {
      sessionManager.setSeasonGameFilter({ mode: 'custom', customGameIds: selectedCustomGameIds });
      return;
    }
    if (selectedGameOption === 'ALL') {
      sessionManager.setSeasonGameFilter({ mode: 'all' });
      return;
    }
    if (selectedGameOption) {
      sessionManager.setSeasonGameFilter({ mode: 'single', gameId: selectedGameOption });
    }
  }, [gameFilterReady, selectedGameOption, selectedCustomGameIds]);

  useEffect(() => {
    if (gamesMode === GAME_MODE_PRACTICE && selectedGameOption === 'LEAGUE') {
      setSelectedGameOption('ALL');
      setSelectedCustomGameIds([]);
    }
  }, [gamesMode, selectedGameOption]);

  const selectableGames = React.useMemo(() => filterGamesByMode(games, gamesMode), [games, gamesMode]);
  const formatGameOptionLabel = useCallback(
    (game: any) => formatLeaderboardGameLabel(game, gamesMode),
    [gamesMode],
  );
  const selectedGameLabel = React.useMemo(() => {
    if (selectedCustomGameIds.length > 1) return `${selectedCustomGameIds.length} Games Selected`;
    if (selectedCustomGameIds.length === 1) {
      const game = selectableGames.find((g) => String(g._id) === selectedCustomGameIds[0]);
      return game ? formatGameOptionLabel(game) : '1 Game Selected';
    }
    if (selectedGameOption === 'LEAGUE') return 'League Games';
    if (selectedGameOption === 'ALL') return 'All Games';
    const game = selectableGames.find((g) => String(g._id) === selectedGameOption);
    return game ? formatGameOptionLabel(game) : 'All Games';
  }, [formatGameOptionLabel, selectableGames, selectedCustomGameIds, selectedGameOption]);
  const selectGamePreset = useCallback((value: 'ALL' | 'LEAGUE') => {
    setSelectedGameOption(value);
    setSelectedCustomGameIds([]);
    setIsGameDropdownOpen(false);
  }, []);
  const toggleSelectedGame = useCallback((game: any) => {
    const gameId = String(game._id);
    setSelectedGameOption('CUSTOM');
    setSelectedCustomGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId],
    );
  }, []);
  const closeGameDropdown = useCallback(() => setIsGameDropdownOpen(false), []);
  useCloseOnClickOutside(gameDropdownRef, isGameDropdownOpen, closeGameDropdown);

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Sticky Quarter Selector */}
      <QuarterSelector periods={periods} activeQuarter={activeQuarter} onSelectQuarter={setActiveQuarter} />

      {/* Main Content */}
      <div className="border border-sd-line overflow-auto">
        {/* Header Section */}
        <div className="relative flex flex-col gap-1 justify-between bg-white">
          <div className="flex justify-center w-full my-4 z-10">
            <WomensFieldMark />
          </div>

          <div className="flex flex-row justify-between items-center text-[30px] mt-[-60px] px-4 font-bold text-start z-10 border-b border-sd-line pb-4">
            <p className="text-sd-navy">Dynamic Leaderboard</p>
            <div
              className={`w-8 h-8 rounded-md p-1 cursor-pointer ${isRefreshing ? 'bg-gray-400' : 'bg-sd-accent'}`}
              onClick={isRefreshing ? undefined : fetchPlayerStats}
              title="Refresh data"
            >
              <img
                src="/icons/icon-refresh.png"
                alt="refresh"
                className={`w-full h-full ${isRefreshing ? 'opacity-50' : ''}`}
              />
            </div>
          </div>
        </div>

        {/* Game filter bar */}
        <div className="flex justify-end px-3 pt-3">
          <div className="relative w-[220px]" ref={gameDropdownRef}>
            <button
              type="button"
              className="bg-white w-full text-sd-navy font-semibold px-3 py-2 rounded-md border border-sd-navy shadow-sm focus:outline-none pr-8 text-sm text-left truncate"
              onClick={() => setIsGameDropdownOpen((prev) => !prev)}
            >
              {selectedGameLabel}
            </button>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="1" y="1" width="18" height="18" rx="4" stroke="rgb(var(--sd-navy))" strokeWidth="2" fill="rgb(var(--sd-navy))" />
                <path d="M7 8l3 3 3-3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            {isGameDropdownOpen && (
              <div className="absolute right-0 mt-1 z-50 w-full max-h-64 overflow-y-auto rounded-md border border-sd-line bg-white shadow-lg p-2">
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 min-h-10 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                  onClick={() => selectGamePreset('ALL')}
                >
                  All Games
                </button>
                {gamesMode !== GAME_MODE_PRACTICE && (
                  <button
                    type="button"
                    className="block w-full rounded-md px-3 py-2 min-h-10 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                    onClick={() => selectGamePreset('LEAGUE')}
                  >
                    League Games
                  </button>
                )}
                <div className="my-1 border-t border-sd-line" />
                {selectableGames.map((g) => {
                  const gid = String(g._id);
                  const checked = selectedCustomGameIds.includes(gid);
                  return (
                    <label
                      key={gid}
                      className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-sd-navy hover:bg-sd-light"
                    >
                      <input
                        type="checkbox"
                        className="h-[18px] w-[18px] shrink-0"
                        checked={checked}
                        onChange={() => toggleSelectedGame(g)}
                      />
                      <span className="truncate">{formatGameOptionLabel(g)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Statistics Tables */}
        <div className="flex flex-col items-center w-full py-4 px-2 bg-gray-50">
          <div className="flex flex-col gap-3 max-w-[800px] w-full">
            <StatsTable
              title="Possession"
              columns={possessionColumns}
              players={possessionPlayers}
              isLoading={isRefreshing}
              teamName={teamName}
              seasonName={seasonName}
              clientId={clientId}
              sortConfig={possessionSortConfig}
              onSortChange={setPossessionSortConfig}
            />
            <StatsTable
              title="Offense"
              columns={offenseColumns}
              players={offensePlayers}
              isLoading={isRefreshing}
              teamName={teamName}
              seasonName={seasonName}
              clientId={clientId}
              sortConfig={offenseSortConfig}
              onSortChange={setOffenseSortConfig}
              cellPaddingY="py-0"
              cellPaddingX="px-0"
            />
            <StatsTable
              title="Goalie"
              columns={goalieColumns}
              players={goaliePlayers}
              isLoading={isRefreshing}
              teamName={teamName}
              seasonName={seasonName}
              clientId={clientId}
              sortConfig={goalieSortConfig}
              onSortChange={setGoalieSortConfig}
            />
            <WomensPlayerTables
              playersData={rawPlayers}
              seasonId={seasonId}
              layout="leaderboard"
              renderTable={({ id, ...table }) => (
                <StatsTable
                  key={id}
                  {...table}
                  isLoading={isRefreshing}
                  teamName={teamName}
                  seasonName={seasonName}
                  clientId={clientId}
                />
              )}
            />
            <StatsTable
              title="Blocked Shots"
              columns={blockedShotsColumns}
              players={blockedShotsPlayers}
              isLoading={isRefreshing}
              teamName={teamName}
              seasonName={seasonName}
              clientId={clientId}
              sortConfig={blockedShotsSortConfig}
              onSortChange={setBlockedShotsSortConfig}
            />
          </div>
        </div>

        {/* Comprehensive Export Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mx-2 mb-6 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Export All Statistics</h3>
              <p className="text-sm text-gray-600">
                Download or email a comprehensive report containing all player statistics
              </p>
            </div>
            <ComprehensiveExportButton
              possessionPlayers={possessionPlayers}
              offensePlayers={offensePlayers}
              goaliePlayers={goaliePlayers}
              faceoffPlayers={faceoffPlayers}
              penaltyPlayers={penaltyPlayers}
              blockedShotsPlayers={blockedShotsPlayers}
              possessionColumns={possessionColumns}
              offenseColumns={offenseColumns}
              goalieColumns={goalieColumns}
              faceoffColumns={faceoffColumns}
              penaltyColumns={penaltyColumns}
              blockedShotsColumns={blockedShotsColumns}
              womensReports={buildWomensExportReports(rawPlayers)}
              teamName={teamName}
              seasonName={seasonName}
              className="w-full sm:w-auto"
              clientId={clientId}
              sortConfigs={{
                possession: possessionSortConfig,
                offense: offenseSortConfig,
                goalie: goalieSortConfig,
                faceoff: faceoffSortConfig,
                penalty: penaltySortConfig,
                blockedShots: blockedShotsSortConfig,
              }}
            />
          </div>
        </div>

        <div className="flex justify-center w-full mb-6 z-10">
          <WomensFieldMark />
        </div>
      </div>
    </div>
  );
};

export default PlayersPageMobile;
